import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Users,
  Bot,
  MessageSquare,
  Sparkles,
  TrendingUp,
  ChevronRight,
  UserPlus,
  Clock,
} from 'lucide-react';
import StatsCard from '../components/data/StatsCard';
import RefreshButton from '../components/layout/RefreshButton';
import { adminApi } from '../services/adminApi';

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [recentRequests, setRecentRequests] = useState([]);
  const [recentFeedback, setRecentFeedback] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  // Real-time stats (users & agents count)
  const [realtimeUsers, setRealtimeUsers] = useState(null);
  const [realtimeAgents, setRealtimeAgents] = useState(null);

  const fetchStats = useCallback(async () => {
    try {
      const response = await adminApi.getStats();
      setStats(response.stats);
      setRecentRequests(response.recentRequests || []);
      setRecentFeedback(response.recentFeedback || []);
      setLastUpdated(new Date());
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  }, []);

  const fetchRealtimeStats = useCallback(async () => {
    try {
      const [usersRes, agentsRes] = await Promise.all([
        adminApi.getUsersCount(),
        adminApi.getAgentsCount(),
      ]);
      setRealtimeUsers(usersRes.count);
      setRealtimeAgents(agentsRes.count);
    } catch (err) {
      console.error('Failed to fetch realtime stats:', err);
    }
  }, []);

  useEffect(() => {
    const loadInitialData = async () => {
      setIsLoading(true);
      await Promise.all([fetchStats(), fetchRealtimeStats()]);
      setIsLoading(false);
    };
    loadInitialData();

    // Poll realtime stats every 30 seconds
    const interval = setInterval(fetchRealtimeStats, 30000);
    return () => clearInterval(interval);
  }, [fetchStats, fetchRealtimeStats]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await fetchStats();
    setIsRefreshing(false);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-text-primary">Dashboard</h1>
          <p className="text-text-secondary mt-1">
            Overview of your platform's performance
          </p>
        </div>
        <RefreshButton
          onClick={handleRefresh}
          isLoading={isRefreshing}
          lastUpdated={lastUpdated}
        />
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatsCard
          title="Total Users"
          value={realtimeUsers ?? stats?.totalUsers ?? 0}
          icon={Users}
          iconBgColor="bg-blue-100"
          iconColor="text-blue-600"
          isLoading={isLoading}
          isRealtime={true}
          trend="up"
          trendValue={stats?.userGrowth || '+0%'}
        />
        <StatsCard
          title="Total Agents"
          value={realtimeAgents ?? stats?.totalAgents ?? 0}
          icon={Bot}
          iconBgColor="bg-purple-100"
          iconColor="text-purple-600"
          isLoading={isLoading}
          isRealtime={true}
          trend="up"
          trendValue={stats?.agentGrowth || '+0%'}
        />
        <StatsCard
          title="Conversations"
          value={stats?.totalConversations ?? 0}
          icon={MessageSquare}
          iconBgColor="bg-green-100"
          iconColor="text-green-600"
          isLoading={isLoading}
        />
        <StatsCard
          title="Custom Requests"
          value={stats?.pendingRequests ?? 0}
          icon={Sparkles}
          iconBgColor="bg-amber-100"
          iconColor="text-amber-600"
          isLoading={isLoading}
        />
      </div>

      {/* Quick Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="card p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-success" />
            </div>
            <div>
              <p className="text-sm text-text-secondary">Active Agents</p>
              {isLoading ? (
                <div className="h-7 w-12 bg-bg-secondary rounded animate-pulse mt-1"></div>
              ) : (
                <p className="text-xl font-bold text-text-primary">
                  {stats?.activeAgents ?? 0}
                </p>
              )}
            </div>
          </div>
          <div className="h-1 bg-bg-secondary rounded-full overflow-hidden">
            <div
              className="h-full bg-success rounded-full"
              style={{
                width: `${
                  stats?.totalAgents
                    ? (stats.activeAgents / stats.totalAgents) * 100
                    : 0
                }%`,
              }}
            ></div>
          </div>
          <p className="text-xs text-text-muted mt-2">
            {stats?.totalAgents
              ? `${Math.round((stats.activeAgents / stats.totalAgents) * 100)}% of total agents`
              : '0% of total agents'}
          </p>
        </div>

        <div className="card p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <UserPlus className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="text-sm text-text-secondary">New Users (7d)</p>
              {isLoading ? (
                <div className="h-7 w-12 bg-bg-secondary rounded animate-pulse mt-1"></div>
              ) : (
                <p className="text-xl font-bold text-text-primary">
                  {stats?.newUsersWeek ?? 0}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="card p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-warning/10 flex items-center justify-center">
              <Clock className="w-5 h-5 text-warning" />
            </div>
            <div>
              <p className="text-sm text-text-secondary">Pending Reviews</p>
              {isLoading ? (
                <div className="h-7 w-12 bg-bg-secondary rounded animate-pulse mt-1"></div>
              ) : (
                <p className="text-xl font-bold text-text-primary">
                  {(stats?.pendingRequests ?? 0) + (stats?.pendingFeedback ?? 0)}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Custom Requests */}
        <div className="card">
          <div className="p-4 border-b border-border flex items-center justify-between">
            <h2 className="font-semibold text-text-primary">Recent Custom Requests</h2>
            <Link
              to="/requests"
              className="text-sm text-primary hover:text-primary-dark flex items-center gap-1"
            >
              View all <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="divide-y divide-border">
            {isLoading ? (
              <div className="p-4">
                <div className="animate-pulse space-y-3">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-bg-secondary rounded-full"></div>
                      <div className="flex-1">
                        <div className="h-4 bg-bg-secondary rounded w-1/3"></div>
                        <div className="h-3 bg-bg-secondary rounded w-1/2 mt-2"></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : recentRequests.length === 0 ? (
              <div className="p-8 text-center text-text-muted">
                No custom requests yet
              </div>
            ) : (
              recentRequests.slice(0, 5).map((request) => (
                <Link
                  key={request.id}
                  to={`/requests/${request.id}`}
                  className="p-4 flex items-center gap-3 hover:bg-bg-secondary/50 transition-colors"
                >
                  <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                    <Sparkles className="w-5 h-5 text-amber-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-text-primary truncate">
                      {request.name}
                    </p>
                    <p className="text-sm text-text-muted truncate">
                      {request.businessName || request.email}
                    </p>
                  </div>
                  <div className="text-right">
                    <span
                      className={`badge ${
                        request.status === 'new'
                          ? 'badge-warning'
                          : request.status === 'contacted'
                          ? 'badge-primary'
                          : 'badge-success'
                      }`}
                    >
                      {request.status}
                    </span>
                    <p className="text-xs text-text-muted mt-1">
                      {formatDate(request.createdAt)}
                    </p>
                  </div>
                </Link>
              ))
            )}
          </div>
        </div>

        {/* Recent Feedback */}
        <div className="card">
          <div className="p-4 border-b border-border flex items-center justify-between">
            <h2 className="font-semibold text-text-primary">Recent Feedback</h2>
            <Link
              to="/feedback"
              className="text-sm text-primary hover:text-primary-dark flex items-center gap-1"
            >
              View all <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="divide-y divide-border">
            {isLoading ? (
              <div className="p-4">
                <div className="animate-pulse space-y-3">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-bg-secondary rounded-full"></div>
                      <div className="flex-1">
                        <div className="h-4 bg-bg-secondary rounded w-1/3"></div>
                        <div className="h-3 bg-bg-secondary rounded w-1/2 mt-2"></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : recentFeedback.length === 0 ? (
              <div className="p-8 text-center text-text-muted">
                No feedback yet
              </div>
            ) : (
              recentFeedback.slice(0, 5).map((feedback) => (
                <Link
                  key={feedback.id}
                  to={`/feedback/${feedback.id}`}
                  className="p-4 flex items-center gap-3 hover:bg-bg-secondary/50 transition-colors"
                >
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <MessageSquare className="w-5 h-5 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-text-primary truncate">
                      {feedback.feedbackType || 'General'}
                    </p>
                    <p className="text-sm text-text-muted truncate">
                      {feedback.message?.slice(0, 50)}...
                    </p>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center gap-1">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <span
                          key={star}
                          className={`text-sm ${
                            star <= (feedback.rating || 0)
                              ? 'text-amber-400'
                              : 'text-gray-200'
                          }`}
                        >
                          ★
                        </span>
                      ))}
                    </div>
                    <p className="text-xs text-text-muted mt-1">
                      {formatDate(feedback.createdAt)}
                    </p>
                  </div>
                </Link>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
